import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Sparkles, Camera, Leaf, TrendingUp, Award } from 'lucide-react';
import { strategyData } from '../mockData';
import { Button } from '../components/ui/button';

const Strategy = () => {
  const iconMap = {
    target: Target,
    sparkles: Sparkles,
    camera: Camera,
    leaf: Leaf
  };

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>The Strategy | Curated Scarcity Marketing for Luxury Glamping</title>
        <meta name="description" content="We don't build; we curate scarcity. Targeting high-net-worth 'Conscious Connoisseurs' seeking authentic digital detox experiences in South Africa." />
        <link rel="canonical" href="https://goglamping.co.za/strategy" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-stone-100 to-sage-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-stone-900 mb-6">
              {strategyData.title}
            </h1>
            <p className="text-2xl text-sage-800 font-semibold mb-6">
              {strategyData.subtitle}
            </p>
            <p className="text-xl text-stone-600 max-w-4xl mx-auto leading-relaxed">
              {strategyData.description}
            </p>
          </div>
        </div>
      </section>

      {/* Strategic Approach */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {strategyData.approach.map((item) => {
              const Icon = iconMap[item.icon] || Sparkles;
              
              return (
                <div
                  key={item.id}
                  className="bg-stone-50 p-8 rounded-2xl border border-stone-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-sage-700 rounded-xl p-3 flex-shrink-0">
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-stone-900 mb-3">
                        {item.title}
                      </h3>
                      <p className="text-stone-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Marketing Strategy */}
      <section className="py-24 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Strategic Marketing: The Scarcity & Social Proof Loop
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Your marketing handles itself through aesthetic excellence and exclusivity
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20">
                <Camera className="h-12 w-12 text-sage-400 mb-4" />
                <h3 className="text-2xl font-bold mb-4">Instagrammable Architecture</h3>
                <p className="text-gray-300 leading-relaxed">
                  The organic, flowing lines of the pod act as a 'content magnet.' One high-end influencer stay can generate 1 million impressions of your land—free marketing that money can't buy.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20">
                <Sparkles className="h-12 w-12 text-sage-400 mb-4" />
                <h3 className="text-2xl font-bold mb-4">Exclusive Platforms</h3>
                <p className="text-gray-300 leading-relaxed">
                  Use all the usual sites but target the less competitive Glamping market. We target 'Slow Travel' curators and wellness platforms where guests pay a premium for 'Under-the-Radar' locations. Your sanctuary becomes a destination, not a commodity.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20">
                <Target className="h-12 w-12 text-sage-400 mb-4" />
                <h3 className="text-2xl font-bold mb-4">The Waitlist Effect</h3>
                <p className="text-gray-300 leading-relaxed">
                  By limiting your land to only a few high-yield units, we create a 'Price Floor.' When there's a waitlist, the R5000 to R6500 rate becomes a non-negotiable standard. Scarcity drives value.
                </p>
              </div>

              <div className="bg-sage-700 p-8 rounded-2xl">
                <h3 className="text-2xl font-bold mb-4">We Don't Advertise—We Curate</h3>
                <p className="text-sage-100 leading-relaxed">
                  Traditional marketing is for traditional businesses. Your land deserves an approach as unique as its beauty. We create demand through exclusivity, not volume. Your customers will become your biggest marketing engine.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KPI Section */}
      <section className="py-24 bg-gradient-to-br from-sage-50 to-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6">
              Measuring Success
            </h2>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto">
              We measure through three specific KPIs, ensuring reality matches the promise
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <TrendingUp className="h-12 w-12 text-sage-700 mb-4" />
              <h3 className="text-2xl font-bold text-stone-900 mb-4">Yield Per Hectare</h3>
              <p className="text-stone-600 leading-relaxed">
                {strategyData.kpis[0]}
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <Leaf className="h-12 w-12 text-sage-700 mb-4" />
              <h3 className="text-2xl font-bold text-stone-900 mb-4">The Pristine Audit</h3>
              <p className="text-stone-600 leading-relaxed">
                {strategyData.kpis[1]}
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <Award className="h-12 w-12 text-sage-700 mb-4" />
              <h3 className="text-2xl font-bold text-stone-900 mb-4">Return on Emotion</h3>
              <p className="text-stone-600 leading-relaxed">
                {strategyData.kpis[2]}
              </p>
            </div>
          </div>

          <div className="bg-sage-700 text-white p-12 rounded-2xl shadow-2xl">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-3xl font-bold mb-4">
                Accountability Built In
              </h3>
              <p className="text-xl text-sage-100 leading-relaxed">
                We don't promise returns—we encourage you to track, measure, and optimize every aspect of your investment. Your success is our reputation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Unseen Business */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src={strategyData.image}
                alt="Strategic approach"
                className="w-full h-[500px] object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/50 to-transparent rounded-2xl" />
            </div>

            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6">
                The "Unseen" Business Model
              </h2>
              <p className="text-xl text-stone-600 mb-6 leading-relaxed">
                The beauty of this model for you, the landowner, is its discreet footprint. While your primary operations continue, this project runs elegantly in the background.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <div className="bg-sage-700 rounded-full p-1 mt-1">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-stone-700 text-lg">Complete privacy maintained on the rest of your property</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-sage-700 rounded-full p-1 mt-1">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-stone-700 text-lg">Guest access carefully curated to designated zones only</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-sage-700 rounded-full p-1 mt-1">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-stone-700 text-lg">Your lifestyle continues undisturbed while income grows</p>
                </div>
              </div>

              <Link to="/contact">
                <Button size="lg" className="bg-sage-700 hover:bg-sage-800 text-white px-6 py-5 text-base rounded-lg shadow-lg hover:scale-105 transition-all duration-300">
                  Let's Discuss Your Strategy
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-sage-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Land?
          </h2>
          <p className="text-xl mb-8 text-sage-100">
            Let's walk your property and identify the highest-yield impact zones
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-sage-700 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg shadow-2xl hover:scale-105 transition-all duration-300">
              Schedule Site Discovery Walk
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Strategy;
